/**
 * DialogOverlayGlobal
 * 
 * Global dark overlay with scroll lock and theme-aware styling.
 * Automatically manages body scroll lock and provides smooth fade animations.
 */

import { AnimatePresence, motion } from 'framer-motion';
import { useEffect } from 'react';
import { useTheme } from 'next-themes';
import { createPortal } from 'react-dom';
import { logger } from "@/lib/logger";

export interface DialogOverlayGlobalProps {
  visible: boolean;
}

/**
 * Global Dialog Overlay Component
 * 
 * Provides:
 * - Theme-aware dark overlay
 * - Body scroll lock
 * - Smooth fade animations
 * - Subtle backdrop blur
 */
export function DialogOverlayGlobal({ visible }: DialogOverlayGlobalProps) {
  const { resolvedTheme } = useTheme();

  // Lock/unlock body scroll
  useEffect(() => {
    if (typeof window === 'undefined') return; // SSR safety

    const body = document.body;
    
    if (visible) {
      // Store current scroll position
      const scrollY = window.scrollY;
      
      // Lock scroll
      body.classList.add('overflow-hidden');
      body.style.position = 'fixed';
      body.style.top = `-${scrollY}px`;
      body.style.width = '100%';
      
      // Debug logging
      if (process.env.NODE_ENV === 'development') {
        logger.log('[DialogOverlay] Scroll locked at position:', scrollY);
      }
      
      return () => {
        // Restore scroll
        const y = body.style.top;
        body.classList.remove('overflow-hidden');
        body.style.position = '';
        body.style.top = '';
        body.style.width = '';
        
        // Restore scroll position
        const restoreY = parseInt(y || '0') * -1;
        window.scrollTo(0, restoreY);
        
        // Debug logging
        if (process.env.NODE_ENV === 'development') {
          logger.log('[DialogOverlay] Scroll restored to position:', restoreY);
        }
      };
    }
  }, [visible]);

  // Theme-aware background
  const overlayColor =
    resolvedTheme === 'dark'
      ? 'bg-black/60 backdrop-blur-[2px]'
      : 'bg-black/35 backdrop-blur-[2px]';

  // Render in portal at document body level (same as dialogs)
  if (typeof document === 'undefined') return null; // SSR safety
  
  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.div
          key="dialog-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className={`fixed inset-0 z-40 pointer-events-none ${overlayColor}`}
          aria-hidden="true"
        />
      )}
    </AnimatePresence>,
    document.body
  );
}
